// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Select from 'react-select';
import Loading from 'react-loading';
import HTML from 'react-render-html';
import { toast } from 'react-toastify';
import Markdown from 'react-markdown/with-html';

import Modal from '../Modal';
import CodeEditor from '../../CodeEditor';
import MapEditor from '../../MapEditor';
import FileUpload from '../../FileUpload';
import SelectStack from '../../SelectStack';
import SelectUser from '../../SelectUser';

import api from '../../../api';
import formatDate from '../../../utilities/formatDate';

import '../../../styles/config.css';

export default class ConfigureModule extends Modal {

    state = {
        config: [],
        configForm: [],
        instructions: "",
        loading: true,
        attaching: false,
        showFullHistory: false, 
    }

    componentDidMount = () => this.getModuleConfigurator(this.props.data.module, this.props.data.stack, this.props.data.edit);

    componentWillReceiveProps = (next) => {
        if (next.data && next.data.module && next.data.module._id && (!this.props.data || !this.props.data.module || !this.props.data.module._id || (next.data.module._id !== this.props.data.module._id)))
            this.getModuleConfigurator(next.data.module, next.data.stack, next.data.edit);
    }

    getModuleConfigurator = async (module, stack, edit) => {

        if (!module || !module._id)
            return;

        await this.setState({ loading: true });

        const res = await api.get('/module/get?moduleId=' + module._id);
        
        if (!res || !res.success || !res.module)
            return;

        // Editing or creating?
        let config = [];
        if (edit !== undefined) {
            const saved = stack.pipeline[edit].config;
            for (const cfg of res.module.config) {
                const ref = saved.find(c => c.label === cfg.label);
                config.push(ref ? ref : {
                    label: cfg.label,
                    value: cfg.default ? cfg.default : ""
                });
            }
        }

        else config = res.module.config.map(c => ({
            label: c.label,
            value: c.default ? c.default : ""
        }));

        await this.setState({
            configForm: res.module.config,
            config,
            instructions: res.module.instructions,
            loading: false,
            attaching: false
        });
    }

    checkRequiredFields = () => {
        // Ensure required fields have a value
        for (const [idx, c] of this.state.configForm.entries())
            if (c.required && c.dataType !== 'auto' && this.state.config[idx].value === "") {
                toast.error('Required fields must not be empty!');
                return false;
            }
        return true;
    }

    editModuleConfig = async () => {
        if (!this.checkRequiredFields())
            return;

        this.setState({ attaching: true });
        const res = await api.post('/module/config', {
            stackId: this.props.data.stack._id,
            stepId: this.props.data.stack.pipeline[this.props.data.edit]._id,
            config: this.state.config
        });
        if (res && res.success)
            this.props.modal();
        this.setState({ attaching: false });

        this.props.data.reload();
    }

    attachModuleToStack = async () => {
        if (!this.checkRequiredFields())
            return;

        this.setState({ attaching: true });
        const res = await api.post('/module/attach', {
            moduleId: this.props.data.module._id,
            stackId: this.props.data.stack._id,
            config: this.state.config
        });
        if (res && res.success)
            this.props.modal();
        this.setState({ attaching: false });
        if (this.props.data.goToStack)
            return window.location.href = '/stack/' + this.props.data.stack._id;
        this.props.data.reload();
    }

    setConfigValue = (idx, value) => {
        let config = [ ...this.state.config ];
        config[idx].value = value;
        this.setState({ config });
    }

    uploadFileExec = async (files, idx) => {
        if (files.length < 1)
            return toast.error("No files to upload!");

        await this.setState({ uploading: true });

        const m = this.props.data && this.props.data.module ? this.props.data.module : {};
        const s = this.props.data && this.props.data.stack ? this.props.data.stack : {};

        // Upload the config file and get key
        let formData = new FormData();

        // Attach file
        formData.set('file', files[0]);

        // Upload!
        const res = await api.post("/module/uploadConfigFile?stackId=" + s._id + "&moduleId=" + m._id, formData);

        if (res && res.key) {
            this.setConfigValue(idx, res.key);
        } else {
            toast.error("Failed to upload config file.");
        }

        this.setState({ uploading: false });
    }

    renderConfigField = (c, idx) => {  
        switch (c.dataType) {
            case "code":
                return (<CodeEditor
                    name={"code-editor-" + String(idx)}
                    className="config-code"
                    style={{  }}
                    theme={this.props.user && this.props.user.enableDarkMode ? "twilight" : "xcode"}
                    value={this.state.config.length > idx ? this.state.config[idx].value : c.default ? c.default : ""}
                    language={c.language ?? "py"}
                    onChange={code => this.setConfigValue(idx, code)}
                    height={c.height ? c.height : ''}
                />);

            case "list":
                return (<Select
                    className="select-container"
                    classNamePrefix="select"
                    value={this.state.config.length > idx ? {
                        value: this.state.config[idx].value,
                        label: this.state.config[idx].value
                    } : { value: c.default ? c.default : "", label: c.default ? c.default : ""}}
                    options={(c.options ? c.options : []).map(o => ({
                        value: o,
                        label: o
                    }))}
                    isSearchable={true}
                    placeholder="Select a value..."
                    onChange={opt => this.setConfigValue(idx, opt.value)}
                />);

            case "map":
                return (<MapEditor
                    value={this.state.config.length > idx ? this.state.config[idx].value : c.default ? c.default : {}}
                    onChange={map => this.setConfigValue(idx, map)}
                />);

            case "file":
                return (<FileUpload
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    stackId={this.props.data?.stack?._id}
                    onChange={file => this.setConfigValue(idx, file)}
                    uploadFile={(files) => this.uploadFileExec(files, idx)}
                    uploading={this.state.uploading}
                />);

            case "stack":
                return (<SelectStack
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={stack => this.setConfigValue(idx, stack)}
                    stacks={this.props.stacks}
                />);

            case "user":
                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={user => this.setConfigValue(idx, user)}
                    users={this.props.users}
                />);
            case "connection.smartvault":
                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={conn => this.setConfigValue(idx, conn)}
                    users={this.props.team?.sync?.smartvault?.map(c => c.folders ? ({
                        _id: c._id,
                        name: c.userEmail,
                        email: c.userEmail
                    }) : null).filter(c => c) ?? []}
                    style={{ height: 120 }}
                />);
            case "connection.box":
                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={conn => this.setConfigValue(idx, conn)}
                    users={this.props.team?.sync?.box?.map(c => c.folders ? ({
                        _id: c._id,
                        name: c.folders[idx].name,
                        email: c.folders[idx].name
                    }) : null).filter(c => c) ?? []}
                    style={{ height: 120 }}
                />);
            case "connection.egnyte":
                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={conn => this.setConfigValue(idx, conn)}
                    users={this.props.team?.sync?.egnyte?.map(c => ({
                        _id: c._id, 
                        name: c.domain,
                        email: c.domain, 
                    }))}
                    style={{ height: 120 }}
                />);
            case "connection.sharepoint":
                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={conn => this.setConfigValue(idx, conn)}
                    users={this.props.team?.sync?.sharepoint?.map(c => c.site ? ({
                        _id: c._id,
                        name: c.site.name,
                        email: c.site.url
                    }) : null).filter(c => c) ?? []}
                    style={{ height: 120 }}
                />);
            case "connection.dropbox":
                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={conn => this.setConfigValue(idx, conn)}
                    users={this.props.team?.sync?.dropbox?.map((c, idx) => c.folders ? ({
                        _id: c._id,
                        name: c.folders[idx].name,
                        email: c.folders[idx].source
                    }) : null).filter(c => c) ?? []}
                    style={{ height: 120 }}
                />);
                case "connection.googledrive":
                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={conn => this.setConfigValue(idx, conn)}
                    users={this.props.team?.sync?.googledrive?.map(c => c.folders ? ({
                        _id: c._id,
                        name: c.folders[idx].name,
                        email: c.folders[idx].source
                    }) : null).filter(c => c) ?? []}
                    style={{ height: 120 }}
                />);

            case "connection.onedrive":
                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={conn => this.setConfigValue(idx, conn)}
                    users={this.props.team?.sync?.onedrive?.map((c, idx) => c.folders ? ({
                        _id: c._id,
                        name: c.folders[idx].name,
                        email: c.folders[idx].source
                    }) : null).filter(c => c) ?? []}
                    style={{ height: 120 }}
                />);
            case "connection.sharefile":

                return (<SelectUser
                    value={this.state.config.length > idx ? this.state.config[idx].value : ""}
                    onChange={conn => this.setConfigValue(idx, conn)}
                    users={this.props.team?.sync?.sharefile?.map(c => c.domain ? ({
                        _id: c._id,
                        name: c.folders[idx].name,
                        email: c.folders[idx].source
                    }) : null).filter(c => c) ?? []}
                    style={{ height: 120 }}
                />);
            default: // String or secret
                return (<input
                    type={c.dataType === "secret" ? "password" : "text"}
                    className="config-field-string"
                    value={this.state.config.length > idx ? this.state.config[idx].value : c.default ? c.default : ""}
                    onChange={e => this.setConfigValue(idx, e.target.value)}    
                />);
        }
    }

    renderLogEvent = (log, idx, user, team) => {
        const userIdx = team.users.findIndex(u => u._id === log.modifiedBy);
        let userName;
        userName = userIdx >= 0 ? team.users[userIdx].name : "Stax.ai Bot";

        const offsetIdx = 8000 + userIdx - team.users.findIndex(u => u._id === user._id);
        
        return (<React.Fragment key={idx}>
            {idx > 0 && <div className="meta-automate-pipeline-pipe" style={{marginLeft: 263}}/>}
            <div className="meta-history-log" title={log.labels.join(', ')} style={{margin: 'auto'}}>
                {userName === "Stax.ai Bot" ? <div className="meta-history-log-icon meta-history-log-icon-image" style={{ backgroundImage: "url('https://static.stax.ai/images/icons/bot.png')" }}></div> : <div
                    className={"meta-history-log-icon palette-color-" + String(offsetIdx%8)}
                    title={idx > 0?`${userName} modified ${log.labels.join(', ')}`: `${userName} attached step to stax`}
                >{userName.substring(0,1)}</div>}
                {log.labels.join(', ')}
                <div className="meta-history-log-user" title={userName}>{userName}</div>
                <div className="meta-history-log-time">{formatDate(log.timestamp)}</div>
            </div>
        </React.Fragment>);
    }

    renderModuleHistory = (history) => {
        if(!history || !history.length)   
            return; 
        return (
        <>
        <div
            className="meta-showmore"
            style={{marginTop: 15}}
            onClick={() => this.setState({ showFullHistory: !this.state.showFullHistory })}
        >   
            {this.state.showFullHistory ? "Collapse history...": "Show history..."}
        </div>
        {this.state.showFullHistory?
            history.map((log, idx) => this.renderLogEvent(log, idx, this.props.user, this.props.team)):''
        }
       </>)
    }

    renderContent = () => {
        const m = this.props.data && this.props.data.module ? this.props.data.module : {};
        const s = this.props.data && this.props.data.stack ? this.props.data.stack : {};
        const h = 'edit' in this.props.data? this.props.stacks?.find(stack => stack._id === this.props.data.stack?._id)?.pipeline[this.props.data.edit]?.history: [];

        return (
            <div
                className='config-container'
            >
                <div>
                    <div className="modal-content modal-center automate-attach">

                        <img
                            className="automate-attach-icon"
                            src={m.icon}
                            alt={m.name}
                        />

                        <h1>{m.name}</h1>
                        <h3>Created by <strong>{m.creatorName}</strong></h3>

                        <h2>Configure Module for: <u>{s.path}</u></h2>

                        {this.state.loading ? <div
                            style={{
                                width: 80,
                                margin: "0 auto",
                                marginTop: -10
                            }}
                        >
                            <Loading
                                type="bubbles"
                                color="var(--color-primary-dark)"
                                height={80}
                                width={80}
                            />
                        </div> : <>
                            {this.state.instructions ? <div className="automate-instructions">
                                {HTML(this.state.instructions)}
                            </div> : ""}
                            {(!this.state.configForm || this.state.configForm.length === 0 ? <p>
                                This module does not need to be configured.
                            </p> : <div className="config-form">
                                {this.state.configForm.map((c, idx) => c.dataType === 'auto' ? <React.Fragment key={idx}></React.Fragment> : <div
                                    key={idx}
                                    className="config"
                                >
                                    <label>{c.label}{c.required && <span className="config-required">(required)</span>}</label>

                                    {this.renderConfigField(c, idx)}

                                    <div style={{ clear: 'both', height: 10 }} />

                                    {c.hint && <div className="hint"><Markdown
                                        source={c.hint}
                                        escapeHtml={false}
                                    /></div>}

                                </div>)}
                            </div>)}
                        </>}

                        <br />
                        {this.state.attaching ? <div
                            style={{
                                width: 80,
                                margin: "0 auto", 
                                marginTop: -10
                            }}
                        >
                            <Loading
                                type="bubbles"
                                color="var(--color-primary-dark)"
                                height={80}
                                width={80}
                            />
                        </div> : (this.props.data.edit === undefined ? <div
                            className="button"
                            style={{
                                float: 'none',
                                backgroundImage: "url('https://static.stax.ai/images/icons/plus.png')"
                            }}
                            onClick={() => this.attachModuleToStack()}
                        >Attach module to stack</div> : <div
                            className="button"
                            style={{
                                float: 'none',
                                backgroundImage: "url('https://static.stax.ai/images/icons/reviewed.png')"
                            }}
                            onClick={() => this.editModuleConfig()}
                        >Save module configuration</div>)}
                        {
                            h.length?<>
                            {this.renderModuleHistory(h)}
                            </>:''
                        }
                    </div>
                </div>
            </div>
        );
    }

};